/**
 * Marks the cells of a row being sized on Cold War (MDOT-25670). While the answer is out, the cell the
 * user typed in shows what was typed, and the row's other sized cells show the values Cold War is about
 * to replace, dimmed. Once a sizing got no answer, the typed-in cell shows its previous value again
 * with a warning saying when it failed and why. A valuation that got no answer flags the market value
 * and weight instead: they are the previous day's, shown dimmed with a warning saying at which day the
 * row could not be valued.
 */
import { AutoTooltip, colorBySeverity, IconTooltip, Row, TooltipContent } from "@mdotm/shadcn/legacy/components";
import type { ColDef } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { useTranslation } from "react-i18next";
import { formatDate, formatDateTime } from "$root/localization/formatters";
import type { Instrument } from "../../const";
import { withCellMarker } from "../withCellMarker";
import type { SizingField } from "./sizing";

/** The cells a failed valuation leaves stale: the price at the day is right, the value is not. */
const STALE_ON_FAILED_VALUATION: ReadonlySet<string | undefined> = new Set<SizingField>(["MARKET_VALUE", "WEIGHT"]);

export function SizingMarkerCell({
	data,
	colDef,
	valueFormatted,
	formatValue,
}: CustomCellRendererProps<Instrument>): React.JSX.Element {
	const { t } = useTranslation();
	const sizing = data?.positionSizing;
	if (sizing == null) {
		return <>{valueFormatted ?? ""}</>;
	}
	const typedHere = sizing.edit.field === colDef?.colId;

	if (sizing.status === "failed" && sizing.valuationDate != null) {
		const stale = STALE_ON_FAILED_VALUATION.has(colDef?.colId);
		return (
			<Row className="h-full w-full" justifyContent="end" alignItems="center" gap={4}>
				{stale && (
					<IconTooltip severity="warning" iconSize={16} hideArrow>
						<TooltipContent>
							<span data-qualifier="CompositionEditor/PositionValuationFailed">
								{t("EDIT_COMPOSITION.POSITION_SIZING.VALUATION_FAILED", {
									date: formatDate(sizing.valuationDate),
									message: sizing.message,
								})}
							</span>
						</TooltipContent>
					</IconTooltip>
				)}
				<span className={stale ? "opacity-50" : undefined}>{valueFormatted ?? ""}</span>
			</Row>
		);
	}

	if (sizing.status === "failed") {
		return (
			<Row className="h-full w-full" justifyContent="end" alignItems="center" gap={4}>
				{typedHere && (
					<IconTooltip severity="warning" iconSize={16} hideArrow>
						<TooltipContent>
							<span data-qualifier="CompositionEditor/PositionSizingFailed">
								{t("EDIT_COMPOSITION.POSITION_SIZING.FAILED", {
									time: formatDateTime(sizing.failedAt),
									message: sizing.message,
								})}
							</span>
						</TooltipContent>
					</IconTooltip>
				)}
				<span>{valueFormatted ?? ""}</span>
			</Row>
		);
	}

	const shown = typedHere ? (formatValue?.(sizing.edit.value) ?? String(sizing.edit.value)) : (valueFormatted ?? "");
	return (
		<Row className="h-full w-full" justifyContent="end" alignItems="center">
			<AutoTooltip
				position="top"
				align="endToEnd"
				severity="info"
				overrideColor={colorBySeverity.info}
				hideArrow
				trigger={({ innerRef }) => (
					<span
						ref={innerRef}
						className={typedHere ? undefined : "opacity-50"}
						aria-busy
						data-qualifier="CompositionEditor/PositionSizing"
					>
						{shown}
					</span>
				)}
			>
				<TooltipContent>{t("EDIT_COMPOSITION.POSITION_SIZING.SIZING")}</TooltipContent>
			</AutoTooltip>
		</Row>
	);
}

/** Layers the sizing marker onto one of the cells a sizing writes: quantity, price, market value or weight. */
export function withSizingMarker(column: ColDef<Instrument>): ColDef<Instrument> {
	return withCellMarker(column, { applies: (row) => row.positionSizing != null, Marker: SizingMarkerCell });
}
